import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";

const MONTH_NAMES = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
const WD_INITIALS = ["D", "L", "Ma", "Mi", "J", "V", "S"];

const curMonth = () => new Date().toISOString().slice(0, 7);

/**
 * Matrice lunară disponibilitate (format Management): nume pe verticală (stânga),
 * zilele lunii pe orizontală, rând „Total disponibili" per zi.
 * Concediu aprobat = celulă roșie „C"; restul = disponibil (verde).
 */
export default function AvailabilityMatrix({ persons, vacations }) {
  const [month, setMonth] = useState(curMonth());
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const todayIso = new Date().toISOString().slice(0, 10);

  const shiftMonth = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  // Ordine cronologică după data angajării → nr. de personal (#1 = cel mai vechi angajat)
  const chronoPersons = useMemo(() => {
    const arr = [...persons].sort((a, b) => {
      const da = a.data_angajarii || "9999-12-31";
      const db = b.data_angajarii || "9999-12-31";
      if (da !== db) return da.localeCompare(db);
      return `${a.nume || ""} ${a.prenume || ""}`.localeCompare(`${b.nume || ""} ${b.prenume || ""}`);
    });
    return arr.map((p, i) => ({ ...p, _nr: i + 1 }));
  }, [persons]);
  const fieldPersons = useMemo(() => chronoPersons.filter((p) => p.office_staff !== true), [chronoPersons]);
  const officePersons = useMemo(() => chronoPersons.filter((p) => p.office_staff === true), [chronoPersons]);
  const fieldIds = useMemo(() => new Set(fieldPersons.map((p) => p.id)), [fieldPersons]);
  const officeIds = useMemo(() => new Set(officePersons.map((p) => p.id)), [officePersons]);

  // vacByDay: iso → Set(person_id) — doar concedii aprobate
  const vacByDay = useMemo(() => {
    const map = {};
    for (const v of vacations || []) {
      if (v.status !== "approved" || !v.person_id || !v.start_date || !v.end_date) continue;
      for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${month}-${String(d).padStart(2, "0")}`;
        if (v.start_date <= iso && iso <= v.end_date) (map[iso] ||= new Set()).add(v.person_id);
      }
    }
    return map;
  }, [vacations, month, daysInMonth]);

  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    const iso = `${month}-${String(d).padStart(2, "0")}`;
    const wd = new Date(y, m - 1, d).getDay();
    return { d, iso, wd, isWeekend: wd === 0 || wd === 6, isToday: iso === todayIso };
  });

  return (
    <div className="card-flat mt-6 overflow-hidden" data-testid="vacation-availability-matrix">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap bg-zinc-50/60">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-emerald-700"/>
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-800">Disponibilitate zilnică — {MONTH_NAMES[m - 1]} {y}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded hover:bg-zinc-100" data-testid="avail-prev-month"><ChevronLeft size={16}/></button>
          <button onClick={() => setMonth(curMonth())} className="px-2 py-1 rounded text-xs font-semibold hover:bg-zinc-100" data-testid="avail-this-month">Luna curentă</button>
          <button onClick={() => shiftMonth(1)} className="p-1.5 rounded hover:bg-zinc-100" data-testid="avail-next-month"><ChevronRight size={16}/></button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="border-collapse w-full">
          <thead className="sticky top-0 z-30">
            <tr className="bg-white">
              <th className="sticky left-0 bg-white p-1.5 z-40 text-[10px] font-bold text-zinc-500 text-left uppercase tracking-wider whitespace-nowrap border-b border-r border-zinc-200 min-w-[160px]">Persoană</th>
              {days.map(({ d, wd, isWeekend, isToday }) => (
                <th key={d} className={`text-center p-1 border-b border-r border-zinc-200 min-w-[34px] ${isToday ? "bg-blue-600 text-white" : isWeekend ? "bg-zinc-900/95 text-white" : "bg-white text-zinc-700"}`}>
                  <div className="text-[11px] font-extrabold tabular-nums">{d}</div>
                  <div className={`text-[8px] uppercase ${isToday ? "text-blue-100" : isWeekend ? "text-zinc-300" : "text-zinc-400"}`}>{WD_INITIALS[wd]}</div>
                </th>
              ))}
            </tr>
            {/* Rând disponibili ȘANTIER */}
            <tr className="bg-zinc-50">
              <th className="sticky left-0 bg-zinc-50 p-1.5 z-40 text-[10px] font-bold text-zinc-700 text-left whitespace-nowrap border-b border-r border-zinc-200" title="Muncitori de șantier activi minus cei în concediu aprobat">👷 Muncitori șantier</th>
              {days.map(({ d, iso, isWeekend, isToday }) => {
                const onVac = [...(vacByDay[iso] || new Set())].filter((pid) => fieldIds.has(pid)).length;
                const avail = Math.max(0, fieldPersons.length - onVac);
                return (
                  <th
                    key={d}
                    className={`text-center text-[11px] font-extrabold border-b border-r border-zinc-200 min-w-[34px] ${isToday ? "bg-blue-50 text-blue-800" : isWeekend ? "bg-zinc-900/95 text-white" : onVac > 0 ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}
                    data-testid={`avail-count-${iso}`}
                    title={`${fieldPersons.length} muncitori − ${onVac} concediu = ${avail} disponibili pe șantier`}
                  >
                    {avail}
                  </th>
                );
              })}
            </tr>
            {/* Rând disponibili BIROU */}
            <tr className="bg-purple-50/60">
              <th className="sticky left-0 bg-purple-50 p-1.5 z-40 text-[10px] font-bold text-purple-800 text-left whitespace-nowrap border-b border-r border-zinc-200" title="Personal birou activ minus cei în concediu aprobat">🏢 Personal birou</th>
              {days.map(({ d, iso, isWeekend, isToday }) => {
                const onVac = [...(vacByDay[iso] || new Set())].filter((pid) => officeIds.has(pid)).length;
                const avail = Math.max(0, officePersons.length - onVac);
                return (
                  <th
                    key={d}
                    className={`text-center text-[11px] font-extrabold border-b border-r border-zinc-200 min-w-[34px] ${isToday ? "bg-blue-50 text-blue-800" : isWeekend ? "bg-zinc-900/95 text-white" : onVac > 0 ? "bg-amber-100 text-amber-900" : "bg-purple-50 text-purple-700"}`}
                    data-testid={`avail-office-count-${iso}`}
                    title={`${officePersons.length} birou − ${onVac} concediu = ${avail} disponibili la birou`}
                  >
                    {avail}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {[...fieldPersons, null, ...officePersons].map((p) => {
              if (p === null) {
                if (officePersons.length === 0) return null;
                return (
                  <tr key="office-separator" className="bg-purple-100/70">
                    <td colSpan={daysInMonth + 1} className="sticky left-0 px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider text-purple-800 border-b border-zinc-200" data-testid="office-separator-row">
                      🏢 Personal birou
                    </td>
                  </tr>
                );
              }
              const name = `${p.nume || ""} ${p.prenume || ""}`.trim();
              return (
                <tr key={p.id} className="hover:bg-zinc-50/60" data-testid={`avail-row-${p.id}`}>
                  <td className="sticky left-0 bg-white p-1.5 z-20 text-[11px] font-semibold text-zinc-800 whitespace-nowrap border-b border-r border-zinc-200">
                    <span
                      className="inline-block min-w-[26px] text-center mr-1.5 text-[9px] font-extrabold text-zinc-500 bg-zinc-100 border border-zinc-200 rounded px-1 tabular-nums"
                      title={p.data_angajarii ? `Nr. personal #${p._nr} — angajat la ${p.data_angajarii}` : `Nr. personal #${p._nr} — fără dată de angajare`}
                      data-testid={`personal-nr-${p.id}`}
                    >
                      #{p._nr}
                    </span>
                    {name}
                  </td>
                  {days.map(({ d, iso, isWeekend, isToday }) => {
                    const onVac = (vacByDay[iso] || new Set()).has(p.id);
                    let cls = "bg-emerald-50/70";
                    let content = "";
                    if (onVac) { cls = "bg-red-500 text-white font-bold"; content = "C"; }
                    else if (isWeekend) { cls = "bg-zinc-100"; }
                    return (
                      <td
                        key={d}
                        className={`text-center text-[10px] border-b border-r border-zinc-200 min-w-[34px] h-6 ${cls} ${isToday && !onVac ? "ring-1 ring-inset ring-blue-400" : ""}`}
                        title={onVac ? `${name} — în concediu pe ${d}/${m}` : `${name} — disponibil pe ${d}/${m}`}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {chronoPersons.length === 0 && (
              <tr><td colSpan={daysInMonth + 1} className="p-4 text-center text-sm text-zinc-400 italic">Nicio persoană activă.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t bg-zinc-50/50 flex flex-wrap gap-4 text-[10px] text-zinc-600">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200 inline-block"/> Disponibil</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500 inline-block"/> C = Concediu aprobat</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-zinc-900 inline-block"/> Weekend</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-600 inline-block"/> Azi</span>
      </div>
    </div>
  );
}
